'use client';

import Image from 'next/image';
import Link from 'next/link';
import { BookOpen, Menu, ChevronDown, LayoutDashboard, Search, Plus, Sparkles, HelpCircle, LogOut } from 'lucide-react';
import { useEffect, useState } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { useSupabase } from '@/components/providers/supabase-provider';
import { professions } from '@/lib/data';
import { getLocaleFromPathname, localizePathname, toFrenchProfessionSlug, translations, type Dictionary } from '@/lib/i18n';
import { Button } from '@/components/ui/button';
import { buttonVariants } from '@/components/ui/button-variants';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { cn } from '@/lib/utils';
import { AuthModal } from '@/components/auth/AuthModal';

export default function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [logoFailed, setLogoFailed] = useState(false);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const { user, authReady, supabase } = useSupabase();

  const router = useRouter();
  const pathname = usePathname();
  const locale = getLocaleFromPathname(pathname);
  const t: Dictionary = translations[locale];
  const localePrefix = `/${locale}`;
  const normalizedPath = pathname.replace(/^\/(fr|en)/, '') || '/';

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 8);
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const storedLocale = typeof window !== 'undefined' ? localStorage.getItem('locale') : null;
    if (storedLocale && storedLocale !== locale) {
      const nextPath = localizePathname(pathname, storedLocale === 'fr' ? 'fr' : 'en');
      router.replace(nextPath);
    }
  }, [locale, pathname, router]);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    router.refresh();
  };

  const handleLocaleToggle = () => {
    const nextLocale = locale === 'fr' ? 'en' : 'fr';
    if (typeof window !== 'undefined') localStorage.setItem('locale', nextLocale);
    router.push(localizePathname(pathname, nextLocale));
  };

  const unlocalizedRoutes = ['/dashboard', '/login', '/register', '/admin'];
  const localizeHref = (href: string) => {
    if (unlocalizedRoutes.some(route => href.startsWith(route))) return href;
    return href === '/' ? localePrefix : `${localePrefix}${href}`;
  };

  const getProfessionHref = (slug: string) =>
    `${localePrefix}/${locale === 'fr' ? toFrenchProfessionSlug(slug) : slug}`;
  const submitToolLabel = locale === 'fr' ? 'Soumettre un outil' : 'Submit Tool';

  const isToolsActive = professions.some((p) => normalizedPath.startsWith(`/${p.slug}`)) || normalizedPath.startsWith('/tools');
  const isHomePage = normalizedPath === '/';
  const aboutHref = isHomePage ? '#about' : localizeHref('/about');

  return (
    <header
      className={cn(
        'sticky top-0 z-50 w-full transition-all duration-300',
        isScrolled
          ? 'bg-background/95 supports-[backdrop-filter]:bg-background/70 border-b border-border/50 backdrop-blur-md shadow-sm'
          : 'bg-transparent border-b border-transparent'
      )}
    >
      <div className="relative mx-auto flex h-16 max-w-7xl items-center px-4 sm:px-6 lg:px-8">

        {/* Left — Logo */}
        <Link href={localizeHref('/')} className="group flex min-w-0 shrink-0 items-center">
          {logoFailed ? (
            <BookOpen className="mr-1.5 h-6 w-6 shrink-0 text-primary transition-transform duration-300 group-hover:scale-110" />
          ) : (
            <Image
              src="/logo.svg"
              alt=""
              width={32}
              height={32}
              className="mr-1.5 h-8 w-8 shrink-0 transition-transform duration-300 group-hover:scale-110 sm:mr-2 sm:h-10 sm:w-10"
              onError={() => setLogoFailed(true)}
              unoptimized
              priority
            />
          )}
          <span className="truncate text-base font-bold tracking-tight text-foreground sm:text-xl">GuideToReviews</span>
        </Link>

        {/* Center — Nav links (absolute so they are truly centered in the bar) */}
        <nav className="absolute left-1/2 hidden -translate-x-1/2 items-center gap-1 md:flex" aria-label="Main navigation">
          <Link
            href={localizeHref('/')}
            className={cn(
              buttonVariants({ variant: 'ghost' }),
              'text-sm font-semibold text-foreground/80 hover:bg-primary/10 hover:text-foreground focus-visible:ring-2 focus-visible:ring-primary',
              normalizedPath === '/' && 'bg-primary/10 text-primary'
            )}
          >
            {t.nav.home}
          </Link>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                className={cn(
                  'text-sm font-semibold text-foreground/80 hover:bg-primary/10 hover:text-foreground focus-visible:ring-2 focus-visible:ring-primary',
                  isToolsActive && 'bg-primary/10 text-primary'
                )}
                aria-label="Open tools menu"
              >
                {t.nav.tools}
                <ChevronDown className="ml-1 h-4 w-4 text-foreground/70" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="min-w-[14rem]">
              <DropdownMenuItem asChild>
                <Link href={localizeHref('/tools')}>All Tools</Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              {professions.map((profession) => (
                <DropdownMenuItem key={profession.id} asChild>
                  <Link href={getProfessionHref(profession.slug)}>
                    {locale === 'fr' ? (t.professions as any)[profession.slug] : profession.name}
                  </Link>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <Link
            href={aboutHref}
            className={cn(
              buttonVariants({ variant: 'ghost' }),
              'text-sm font-semibold text-foreground/80 hover:bg-primary/10 hover:text-foreground focus-visible:ring-2 focus-visible:ring-primary',
              normalizedPath === '/about' && 'bg-primary/10 text-primary'
            )}
          >
            {t.nav.about}
          </Link>

          <Link
            href={localizeHref('/insights')}
            className={cn(
              buttonVariants({ variant: 'ghost' }),
              'text-sm font-semibold text-foreground/80 hover:bg-primary/10 hover:text-foreground focus-visible:ring-2 focus-visible:ring-primary',
              normalizedPath === '/insights' && 'bg-primary/10 text-primary'
            )}
          >
            Insights
          </Link>

          <Link
            href={localizeHref('/submit-tool')}
            className={cn(
              buttonVariants({ variant: 'ghost' }),
              'text-sm font-semibold text-foreground/80 hover:bg-primary/10 hover:text-foreground focus-visible:ring-2 focus-visible:ring-primary',
              normalizedPath === '/submit-tool' && 'bg-primary/10 text-primary'
            )}
          >
            {submitToolLabel}
          </Link>
        </nav>

        {/* Right — Auth + Locale (ml-auto pushes to far right) */}
        <div className="ml-auto hidden items-center gap-2 md:flex">
          {!authReady ? (
            <div className="h-9 w-16 animate-pulse rounded-md bg-muted" aria-hidden="true" />
          ) : user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  className="h-9 border-border/50 bg-primary/10 text-foreground hover:bg-primary/15 focus-visible:ring-2 focus-visible:ring-primary"
                  aria-label="Open account menu"
                >
                  Account
                  <ChevronDown className="ml-1 h-4 w-4 text-foreground/70" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-[13rem]">
                <DropdownMenuItem asChild>
                  <Link href={localizeHref('/dashboard')} className="flex items-center gap-2 cursor-pointer font-semibold">
                    <LayoutDashboard className="h-4 w-4 text-muted-foreground" />
                    Dashboard
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link href={localizeHref('/')} className="flex items-center gap-2 cursor-pointer">
                    <Search className="h-4 w-4 text-muted-foreground" />
                    Browse Tools
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link href={localizeHref('/submit-tool')} className="flex items-center gap-2 cursor-pointer">
                    <Plus className="h-4 w-4 text-muted-foreground" />
                    Submit a Tool
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => window.dispatchEvent(new Event('open-ai-assistant'))}
                  className="flex items-center gap-2 cursor-pointer"
                >
                  <Sparkles className="h-4 w-4 text-primary" />
                  AI Assistant
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link href={localizeHref('/help')} className="flex items-center gap-2 cursor-pointer">
                    <HelpCircle className="h-4 w-4 text-muted-foreground" />
                    Help Center
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="flex items-center gap-2 cursor-pointer text-error font-semibold">
                  <LogOut className="h-4 w-4" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button
              onClick={() => setIsAuthModalOpen(true)}
              className={cn(
                buttonVariants({ variant: 'default' }),
                'h-9 bg-primary text-primary-foreground font-semibold shadow-sm hover:bg-primary/90 focus-visible:ring-2 focus-visible:ring-primary transition-all hover:scale-105 active:scale-95'
              )}
            >
              Login
            </Button>
          )}

          <Button
            variant="outline"
            className="h-9 border-border/50 bg-primary/10 px-3 text-xs font-bold text-foreground hover:bg-primary/15 focus-visible:ring-2 focus-visible:ring-primary"
            onClick={handleLocaleToggle}
            aria-label="Toggle language"
          >
            {locale === 'fr' ? 'EN' : 'FR'}
          </Button>
        </div>

        <div className="ml-auto flex items-center gap-2 md:hidden">
          <Sheet open={isMenuOpen} onOpenChange={setIsMenuOpen}>
            <SheetTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                aria-label="Open menu"
                className="text-foreground"
              >
                <Menu className="h-7 w-7" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-full sm:w-80 bg-background border-l border-border/50 p-6 overflow-y-auto">
              <SheetHeader className="text-left mb-6">
                <SheetTitle className="text-xl font-bold tracking-tight flex items-center">
                  {logoFailed ? (
                    <BookOpen className="mr-2 h-6 w-6 text-primary" />
                  ) : (
                    <Image
                      src="/logo.svg"
                      alt=""
                      width={24}
                      height={24}
                      className="mr-2"
                      onError={() => setLogoFailed(true)}
                      unoptimized
                    />
                  )}
                  GuideToReviews
                </SheetTitle>
              </SheetHeader>
              <div className="space-y-2">
                <Link
                  href={localizeHref('/')}
                  className={cn("flex w-full justify-start rounded-md px-4 py-2.5 text-base font-semibold transition-colors", normalizedPath === '/' ? "bg-primary/10 text-primary" : "text-foreground/80 hover:bg-primary/5 hover:text-foreground")}
                  onClick={() => setIsMenuOpen(false)}
                >
                  {t.nav.home}
                </Link>
                <Link
                  href={aboutHref}
                  className={cn("flex w-full justify-start rounded-md px-4 py-2.5 text-base font-semibold transition-colors", normalizedPath === '/about' ? "bg-primary/10 text-primary" : "text-foreground/80 hover:bg-primary/5 hover:text-foreground")}
                  onClick={() => setIsMenuOpen(false)}
                >
                  {t.nav.about}
                </Link>
                <Link
                  href={localizeHref('/insights')}
                  className={cn("flex w-full justify-start rounded-md px-4 py-2.5 text-base font-semibold transition-colors", normalizedPath === '/insights' ? "bg-primary/10 text-primary" : "text-foreground/80 hover:bg-primary/5 hover:text-foreground")}
                  onClick={() => setIsMenuOpen(false)}
                >
                  Insights
                </Link>
                <Link
                  href={localizeHref('/submit-tool')}
                  className={cn("flex w-full justify-start rounded-md px-4 py-2.5 text-base font-semibold transition-colors", normalizedPath === '/submit-tool' ? "bg-primary/10 text-primary" : "text-foreground/80 hover:bg-primary/5 hover:text-foreground")}
                  onClick={() => setIsMenuOpen(false)}
                >
                  {submitToolLabel}
                </Link>
                
                <div className="my-4 border-t border-border/50 pt-4 space-y-2">
                  <span className="block px-4 py-1.5 text-xs font-bold uppercase tracking-widest text-muted-foreground">
                    Tools
                  </span>
                  <Link
                    href={localizeHref('/tools')}
                    className={cn("flex w-full justify-start rounded-md px-4 py-2.5 text-base font-semibold transition-colors", normalizedPath === '/tools' ? "bg-primary/10 text-primary" : "text-foreground/80 hover:bg-primary/5 hover:text-foreground")}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    All Tools
                  </Link>
                  {professions.map((profession) => {
                    const profHref = getProfessionHref(profession.slug);
                    const isActive = normalizedPath.startsWith(`/${profession.slug}`);
                    return (
                      <Link
                        key={profession.id}
                        href={profHref}
                        className={cn("flex w-full justify-start rounded-md px-4 py-2.5 text-base font-semibold transition-colors", isActive ? "bg-primary/10 text-primary" : "text-foreground/80 hover:bg-primary/5 hover:text-foreground")}
                        onClick={() => setIsMenuOpen(false)}
                      >
                        {locale === 'fr' ? (t.professions as any)[profession.slug] : profession.name}
                      </Link>
                    );
                  })}
                </div>

                <div className="my-4 border-t border-border/50 pt-4 space-y-2">
                  {user ? (
                    <>
                      <Link
                        href={localizeHref('/dashboard')}
                        className={cn("flex w-full justify-start rounded-md px-4 py-2.5 text-base font-semibold transition-colors", normalizedPath === '/dashboard' ? "bg-primary/10 text-primary" : "text-foreground/80 hover:bg-primary/5 hover:text-foreground")}
                        onClick={() => setIsMenuOpen(false)}
                      >
                        Dashboard
                      </Link>
                      <button
                        type="button"
                        onClick={async () => {
                          await handleLogout();
                          setIsMenuOpen(false);
                        }}
                        className="flex w-full justify-start rounded-md px-4 py-2.5 text-base font-semibold text-foreground/80 hover:bg-primary/5 hover:text-foreground transition-colors"
                      >
                        Logout
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      onClick={() => {
                        setIsAuthModalOpen(true);
                        setIsMenuOpen(false);
                      }}
                      className="flex w-full justify-start rounded-md px-4 py-2.5 text-base font-semibold text-foreground/80 hover:bg-primary/5 hover:text-foreground transition-colors"
                    >
                      Login
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => {
                      handleLocaleToggle();
                      setIsMenuOpen(false);
                    }}
                    className="flex w-full justify-start rounded-md px-4 py-2.5 text-base font-semibold text-foreground/80 hover:bg-primary/5 hover:text-foreground transition-colors"
                  >
                    {locale === 'fr' ? 'Switch to English' : 'Passer en francais'}
                  </button>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
      <AuthModal 
        isOpen={isAuthModalOpen} 
        onClose={() => setIsAuthModalOpen(false)} 
      />
    </header>
  );
}
